import { Box, Button, Typography } from '@mui/material'
import QuestionOptionsTemplate from 'components/templates/QuestionOptionsTemplate'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import colors from 'styles/colors'
import CommonDialog from '../notificationOverlay/CommonDialog'
import ImageContainer from 'components/atoms/container/ImageContainer'
import { getConstant } from 'services/constantServices'
import { CONSTANTS_OPTIONS } from 'services/constants'
import RadioOption from 'components/atoms/input/RadioOption'
import InCardStack from 'components/atoms/container/InCardStack'
import {
  AttemptsBanner,
  MAX_ATTEMPTS,
  PASS_PERCENTAGE
} from './QuizCheckMolecules'

const StepQuizCheck = ({
  stepData,
  onSubmitLearningCheck,
  setShowStepper,
  contentActionTracking,
  setAnswersFromLocalStorage,
  questions,
  answers,
  handleAnswerChange,
  submitError,
  handleLearningCheckReset,
  scrollToTop
}) => {
  const { t } = useTranslation()
  const [answersArray, setAnswersArray] = useState([])
  const [passed, setPassed] = useState(false)
  const [correctCheckCount, setCorrectCheckCount] = useState(0)
  const [quizResultMessages, setQuizResultMessages] = useState([])
  const [badgePopupOpen, setBadgePopupOpen] = useState(false)
  const [quizResultMessageDisplay, setQuizResultMessageDisplay] = useState(null)
  const [displayAnswerIndex, setDisplayAnswerIndex] = useState(null)

  const calculateCheckScores = (quest, ans) => {
    const resultsMatch = quest.filter((q, i) => {
      if (q.correctIndex.toString() === ans[i].toString()) {
        return true
      } else {
        return false
      }
    })
    setCorrectCheckCount(resultsMatch.length)
    const correctPercent = resultsMatch.length / quest.length
    if (correctPercent >= PASS_PERCENTAGE) {
      setPassed(true)
      setShowStepper(true)
    }
    return correctPercent
  }

  const getResultMessageDisplay = correctPercent => {
    const quizResultGroup = quizResultMessages.find(
      q => q.minScore <= correctPercent
    )
    if (quizResultGroup) {
      quizResultGroup.displayMessage =
        quizResultGroup.messages[
          Math.floor(Math.random() * quizResultGroup.messages.length)
        ]
      setQuizResultMessageDisplay(quizResultGroup)
    }
  }

  useEffect(() => {
    const processData = async () => {
      setAnswersArray([])
      setDisplayAnswerIndex(null)

      if (stepData.userQuizResults?.length > 0) {
        const resultArray = []
        let maxScore = 0
        stepData.userQuizResults.forEach(r => {
          const score = calculateCheckScores(questions, r.resultData)
          resultArray.push({ resultData: r.resultData, score, submitted: true })
          if (score > maxScore) {
            maxScore = score
          }
        })
        setAnswersArray(resultArray)
        setDisplayAnswerIndex(resultArray.findIndex(r => r.score === maxScore))
      }
      if (
        !stepData.userQuizResults ||
        stepData.userQuizResults.length < MAX_ATTEMPTS
      ) {
        const previousAnswers = setAnswersFromLocalStorage()        
        if (previousAnswers && previousAnswers.length > 0) {
          setDisplayAnswerIndex(null)
        }
      }
      const res = await getConstant(CONSTANTS_OPTIONS.QUIZ_RESULT_MESSAGES)
      if (res) {
        setQuizResultMessages(res)
      }
    }
    if (stepData && questions?.length) {
      processData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepData?.id, questions])

  useEffect(() => {
    if (answersArray?.length >= MAX_ATTEMPTS) {
      setShowStepper(true)
    }
  }, [answersArray, setShowStepper])

  const handleSubmit = async () => {
    // setShowStepper(true)
    const submitted = await onSubmitLearningCheck()
    if (submitted) {
      const score = calculateCheckScores(questions, answers)
      getResultMessageDisplay(score)
      const result = { resultData: answers, score, submitted: true }
      setAnswersArray([...answersArray, result])
      stepData.userQuizResults.push(result)
      if (score === 1 || answersArray.length === MAX_ATTEMPTS - 1) {
        setBadgePopupOpen(true)
      }
      setDisplayAnswerIndex(answersArray.length)
      contentActionTracking('Complete Learning Check')
    }
  }

  const handleRetake = () => {
    handleLearningCheckReset()
    setDisplayAnswerIndex(null)
    scrollToTop()
  }

  const maxAttemptReached = stepData.userQuizResults?.length >= MAX_ATTEMPTS
  const Badge = ({ size }) => (
    <Box width={size}>
      <ImageContainer src={quizResultMessageDisplay?.badge} alt='score-badge' />
    </Box>
  )

  const QuestionsBody = ({ submitted }) => {
    return (
      <>
        {questions.map((q, i) => {
          if (submitted && answersArray[displayAnswerIndex]?.resultData) {
            const displayAnswers = answersArray[displayAnswerIndex]?.resultData
            return (
              <QuestionOptionsTemplate
                key={`question-${i}`}
                question={`${i + 1}. ${q.question}`}
                initialValue={displayAnswers[i]?.toString()}
                correctAnswer={q.correctIndex?.toString()}
                correctMessage={
                  maxAttemptReached &&
                  (q.correctSummary || t('stepQuiz.correct'))
                } // Set this to false can hide summary banner
                wrongMessage={
                  q.wrongSummary ||
                  `${t('stepQuiz.incorrectAnswer')} ${q.options?.[q.correctIndex]}`
                }
                showSummary={true}
                options={q.options?.map((a, j) => {
                  const fontWeight = displayAnswers[i] === j ? 'bold' : 'normal'
                  return (
                    <RadioOption
                      value={j}
                      label={a}
                      disabled={true}
                      key={`option-${i}-${j}`}
                      sx={{
                        gap: 2,
                        '& .MuiTypography-root, .MuiTypography-root.Mui-disabled':
                          {
                            fontWeight,
                            color:
                              displayAnswers[i] === j
                                ? displayAnswers[i] === q.correctIndex
                                  ? colors.Green400
                                  : colors.Watermelon400
                                : colors.MidnightTeal400
                          }
                      }}
                    />
                  )
                })}
              />
            )
          }
          return (
            <QuestionOptionsTemplate
              key={`question-${i}`}
              question={`${i + 1}. ${q.question}`}
              initialValue={answers[i]?.toString()}
              handleOnValueChange={handleAnswerChange(i)}
              submitError={submitError}
              options={q.options?.map((a, j) => (
                <RadioOption
                  value={j}
                  label={a}
                  key={`option-${i}-${j}`}
                  sx={{
                    gap: 2,
                    '& .MuiRadio-root': {
                      padding: 2,
                      marginLeft: 3
                    }
                  }}
                />
              ))}
            />
          )
        })}
      </>
    )
  }

  const fullScore = Boolean(answersArray.find(a => a.score === 1))
  return (
    <InCardStack spacing={8}>
      <AttemptsBanner
        answersArray={answersArray}
        displayAnswerIndex={displayAnswerIndex}
        setDisplayAnswerIndex={setDisplayAnswerIndex}
        passed={passed}
        fullScore={fullScore}
        handleRetake={handleRetake}
      />
      {questions?.length && (
        <>
          {displayAnswerIndex === null ? (
            <QuestionsBody submitted={false} />
          ) : (
            <QuestionsBody submitted={true} />
          )}
        </>
      )}
      {!maxAttemptReached && displayAnswerIndex !== null && !fullScore && (
        <Button onClick={handleRetake} color='muted'>
          {t('stepQuiz.retake')}
        </Button>
      )}
      {!maxAttemptReached && displayAnswerIndex === null ? (
        <Button onClick={handleSubmit} sx={{ width: 'fit-content' }}>
          {t('checkInSurvey.submitAnswers')}
        </Button>
      ) : null}
      <CommonDialog
        open={badgePopupOpen}
        maxWidth='sm'
        bodyWidth={392}
        confirmText={t('rolePlay.viewYourResults')}
        onClose={() => setBadgePopupOpen(false)}>
        <InCardStack sx={{ width: 342 }} spacing={12}>
          <Badge size='180px' />
          <Typography fontWeight='bold'>
            {t('stepQuiz.youScored')} {correctCheckCount}/{questions.length}
          </Typography>
          <InCardStack spacing={6} textAlign='center'>
            <Typography fontWeight={600}>{stepData?.title}</Typography>
            <Typography>
              {quizResultMessageDisplay?.displayMessage?.body}
            </Typography>
            <Typography>
              {quizResultMessageDisplay?.displayMessage?.closing}
            </Typography>
          </InCardStack>
        </InCardStack>
      </CommonDialog>
    </InCardStack>
  )
}

export default StepQuizCheck
